import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPropertiesDashboard, addProperty, getPropertyImageUrl, getLockStatus } from '../services/api';
import {
  CHECK_OUT_TIME_OPTIONS, CHECK_IN_TIME_OPTIONS, CLEANING_BUFFER_OPTIONS,
  PROPERTY_TYPE_OPTIONS, BEDROOM_OPTIONS, BATHROOM_OPTIONS,
  generateSpacesConfig, DEFAULT_NEW_PROPERTY,
} from '../utils/propertyConstants';
import LoadingScreen from '../components/LoadingScreen';

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lockData, setLockData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProperty, setNewProperty] = useState({ ...DEFAULT_NEW_PROPERTY });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const props = await getPropertiesDashboard();
      setProperties(props || []);
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      setLoading(false);
    }
    getLockStatus().then(setLockData).catch(() => setLockData(null));
  };

  const handleAddProperty = async () => {
    if (!newProperty.name || !newProperty.address) return;
    setSaving(true);
    try {
      const result = await addProperty(newProperty);
      setShowAddModal(false);
      setNewProperty({ ...DEFAULT_NEW_PROPERTY });
      // Navigate to the new property's settings tab
      if (result?.id) {
        navigate(`/properties/${result.id}/manage`);
      } else {
        fetchData();
      }
    } catch (err) {
      console.error('Error creating property:', err);
      alert('Failed to create property.');
    } finally {
      setSaving(false);
    }
  };

  const filteredProperties = properties.filter(p =>
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.city || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <LoadingScreen message="Loading properties..." />;

  return (
    <div className="ayni-page">
      {/* Header */}
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Properties</h1>
        <p className="ayni-statline" style={{ marginTop: 8 }}>
          <strong>{properties.length}</strong>{properties.length === 1 ? 'property' : 'properties'}
        </p>
      </div>

      {/* Toolbar */}
      <div className="ayni-toolbar">
        <div className="ayni-toolbar-group" />
        <div className="ayni-toolbar-group">
          <input
            className="ayni-search"
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="ayni-btn primary" onClick={() => setShowAddModal(true)}>
            + Add Property
          </button>
        </div>
      </div>

      {/* Properties List */}
      {filteredProperties.length === 0 ? (
        <div className="ayni-empty">No properties found. Click "+ Add Property" to create one.</div>
      ) : (
        <div className="ayni-card">
          <table className="ayni-table">
            <thead>
              <tr>
                <th style={{ width: 76 }} />
                <th>Listing</th>
                <th>Location</th>
                <th>Type</th>
                <th>Status</th>
                <th>Lock</th>
                <th>Aynistas</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {filteredProperties.map((property) => {
                const lock = property.seam_device_id && lockData?.locks?.find(l => l.device_id === property.seam_device_id);
                const pct = lock?.battery_percent ?? null;
                const batColor = pct != null ? (pct > 50 ? '#22c55e' : pct > 20 ? '#eab308' : '#ef4444') : null;
                const st = (property.status || '').toLowerCase();
                const tone = st === 'occupied' ? 'red' : st === 'vacant' ? 'green' : '';

                return (
                  <tr key={property.id} onClick={() => navigate(`/properties/${property.id}/manage`)}>
                    <td style={{ width: 76 }}>
                      {property.image_url ? (
                        <img className="ayni-prop-thumb" src={getPropertyImageUrl(property.image_url)} alt="" />
                      ) : (
                        <div className="ayni-prop-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fa-solid fa-building" style={{ fontSize: 18, color: 'var(--text-muted)' }} />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="ayni-rowname">{property.name}</div>
                      <div className="ayni-prop-addr">{property.address}</div>
                    </td>
                    <td className="ayni-meta">{property.city || '—'}</td>
                    <td className="ayni-meta">{property.property_type || '—'}</td>
                    <td><span className={`ayni-badge ${tone}`}>{property.status || 'Unknown'}</span></td>
                    <td>
                      {lock ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className={lock.locked ? 'fa-solid fa-lock' : 'fa-solid fa-lock-open'} style={{ fontSize: 11, color: 'var(--text-muted)' }} />
                          <div style={{ flex: 1, minWidth: 28, height: 4, background: 'var(--border-light)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: batColor, borderRadius: 2 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: batColor }}>{pct}%</span>
                        </div>
                      ) : (
                        <span className="ayni-meta">—</span>
                      )}
                    </td>
                    <td className="ayni-meta">
                      {property.assigned_cleaner_name ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <i className="fa-solid fa-user" style={{ fontSize: 10, color: 'var(--badge-green-text)' }} />
                          {property.assigned_cleaner_name}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                      <i className="fa-solid fa-chevron-right" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Property Modal */}
      {showAddModal && (
        <div className="ayni-modal" onClick={() => setShowAddModal(false)}>
          <div className="ayni-modal-panel" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
            <h2 className="ayni-modal-title">New Property</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="ayni-label">Name *</label>
                <input className="ayni-input" value={newProperty.name} onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })} placeholder="Property name" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="ayni-label">Address *</label>
                <input className="ayni-input" value={newProperty.address} onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })} placeholder="Street address" />
              </div>
              <div>
                <label className="ayni-label">City</label>
                <input className="ayni-input" value={newProperty.city} onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })} />
              </div>
              <div>
                <label className="ayni-label">Country</label>
                <input className="ayni-input" value={newProperty.country} onChange={(e) => setNewProperty({ ...newProperty, country: e.target.value })} />
              </div>
              <div>
                <label className="ayni-label">Zip Code</label>
                <input className="ayni-input" value={newProperty.zip_code} onChange={(e) => setNewProperty({ ...newProperty, zip_code: e.target.value })} />
              </div>
              <div>
                <label className="ayni-label">Type</label>
                <select className="ayni-select" value={newProperty.property_type} onChange={(e) => setNewProperty({ ...newProperty, property_type: e.target.value })}>
                  {PROPERTY_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="ayni-label">iCal URL</label>
                <input className="ayni-input" value={newProperty.ical_url} onChange={(e) => setNewProperty({ ...newProperty, ical_url: e.target.value })} placeholder="https://..." />
              </div>
            </div>

            {/* Scheduling */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <div style={{ flex: 1 }}>
                <label className="ayni-label">Check-out</label>
                <select className="ayni-select" value={newProperty.check_out_time} onChange={(e) => setNewProperty({ ...newProperty, check_out_time: e.target.value })}>
                  {CHECK_OUT_TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="ayni-label">Check-in</label>
                <select className="ayni-select" value={newProperty.check_in_time} onChange={(e) => setNewProperty({ ...newProperty, check_in_time: e.target.value })}>
                  {CHECK_IN_TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="ayni-label">Buffer</label>
                <select className="ayni-select" value={newProperty.cleaning_buffer} onChange={(e) => setNewProperty({ ...newProperty, cleaning_buffer: parseInt(e.target.value) })}>
                  {CLEANING_BUFFER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Room Structure */}
            <div style={{ marginTop: '16px' }}>
              <label className="ayni-label">Room Structure</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '4px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Bedrooms</span>
                  <select className="ayni-select" value={newProperty.num_bedrooms || 1}
                    onChange={(e) => { const v = parseInt(e.target.value); setNewProperty(p => ({ ...p, num_bedrooms: v, spaces_config: generateSpacesConfig(v, p.num_bathrooms || 1, p.has_kitchen !== false, p.has_living_room !== false) })); }}>
                    {BEDROOM_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Bathrooms</span>
                  <select className="ayni-select" value={newProperty.num_bathrooms || 1}
                    onChange={(e) => { const v = parseInt(e.target.value); setNewProperty(p => ({ ...p, num_bathrooms: v, spaces_config: generateSpacesConfig(p.num_bedrooms || 1, v, p.has_kitchen !== false, p.has_living_room !== false) })); }}>
                    {BATHROOM_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Kitchen</span>
                  <select className="ayni-select" value={newProperty.has_kitchen !== false ? 'yes' : 'no'}
                    onChange={(e) => { const v = e.target.value === 'yes'; setNewProperty(p => ({ ...p, has_kitchen: v, spaces_config: generateSpacesConfig(p.num_bedrooms || 1, p.num_bathrooms || 1, v, p.has_living_room !== false) })); }}>
                    <option value="yes">Yes</option><option value="no">No</option>
                  </select>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Living Room</span>
                  <select className="ayni-select" value={newProperty.has_living_room !== false ? 'yes' : 'no'}
                    onChange={(e) => { const v = e.target.value === 'yes'; setNewProperty(p => ({ ...p, has_living_room: v, spaces_config: generateSpacesConfig(p.num_bedrooms || 1, p.num_bathrooms || 1, p.has_kitchen !== false, v) })); }}>
                    <option value="yes">Yes</option><option value="no">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="ayni-modal-actions">
              <button
                className="ayni-btn primary"
                onClick={handleAddProperty}
                disabled={saving || !newProperty.name || !newProperty.address}
              >
                {saving ? 'Creating...' : 'Create Property'}
              </button>
              <button
                className="ayni-btn ghost"
                onClick={() => { setShowAddModal(false); setNewProperty({ ...DEFAULT_NEW_PROPERTY }); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
